import React from "react";
import {
  Alert,
  AlertIcon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  RadioGroup,
  Radio,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Subscription, StripeSubscriptionDetail } from "./types";
import { isAllAccessType } from "./allAccess";
import { billingPrice } from "./BillingInterval";

export type TrialCancellationAction = "end_of_trial" | "immediate" | "undo";
export const trialDeadlineLabel = (end: string) =>
  new Date(end).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });

export function TrialCancellationDialog({
  subscription,
  detail,
  initialAction,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  subscription: Subscription;
  detail?: StripeSubscriptionDetail;
  initialAction: TrialCancellationAction;
  busy: boolean;
  error: string;
  onClose: () => void;
  onConfirm: (action: TrialCancellationAction) => void;
}) {
  const [action, setAction] = React.useState(initialAction);
  const upgrade = subscription.trialSource === "paid_upgrade_overlay";
  const allAccess = isAllAccessType(subscription.type);
  const plan = allAccess
    ? "ALL-ACCESS"
    : `${subscription.type.toUpperCase()} GOAT`;
  const deadline = trialDeadlineLabel(subscription.trialEnd!);
  const undo = action === "undo";
  const scheduled =
    subscription.trialConversionStatus === "cancel_scheduled" ||
    detail?.cancelAtPeriodEnd;
  const expired = new Date(subscription.trialEnd!).getTime() <= Date.now();
  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onClose}
      closeOnOverlayClick={!busy}
      isCentered
    >
      <ModalOverlay />
      <ModalContent mx={4}>
        <ModalHeader>
          {undo ? "Undo cancellation" : `Cancel ${plan} trial`}
        </ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          <Stack spacing={4}>
            {undo ? (
              <>
                <Text>
                  Your trial still ends on {deadline}. It will automatically
                  convert to {plan} at{" "}
                  {billingPrice(
                    subscription.type,
                    "paid+",
                    detail?.trialBillingInterval ||
                      detail?.billingInterval ||
                      "month"
                  )}
                  .
                </Text>
                {upgrade && (
                  <Text>
                    Your ALL-STAR plan will upgrade to GOAT. Any applicable
                    credit for unused ALL-STAR time is applied.
                  </Text>
                )}
              </>
            ) : (
              <>
                {!scheduled && !subscription.trialPendingAction && (
                  <RadioGroup
                    value={action}
                    onChange={(value) =>
                      setAction(value as TrialCancellationAction)
                    }
                    isDisabled={busy}
                  >
                    <Stack spacing={4}>
                      <Radio value="end_of_trial">
                        <Text fontWeight="semibold">Cancel at trial end</Text>
                        <Text fontSize="sm">
                          Keep {plan} until {deadline}.
                        </Text>
                      </Radio>
                      <Radio value="immediate">
                        <Text fontWeight="semibold">End trial now</Text>
                        <Text fontSize="sm">End trial access immediately.</Text>
                      </Radio>
                    </Stack>
                  </RadioGroup>
                )}
                <Text>
                  {action === "immediate"
                    ? "Trial access ends now. This trial cannot be restarted."
                    : "You can undo cancellation before the trial ends."}
                </Text>
                <Text>
                  {upgrade
                    ? `You won't be charged for the GOAT upgrade. Your ALL-STAR subscription ${
                        action === "immediate"
                          ? "continues now"
                          : "continues after the trial"
                      } on its existing billing schedule.`
                    : `You won't be charged for this trial's paid plan. ${
                        allAccess
                          ? "Any subscriptions paused for this trial resume when it ends."
                          : "Your other subscriptions remain active."
                      }`}
                </Text>
              </>
            )}
            {(error || expired) && (
              <Alert status="error">
                <AlertIcon />
                {error ||
                  "This trial has ended. Close this dialog and refresh your subscriptions."}
              </Alert>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={3} flexWrap="wrap" justifyContent="flex-end">
          <Button variant="ghost" onClick={onClose} isDisabled={busy}>
            Keep current plan
          </Button>
          <Button
            colorScheme={action === "immediate" ? "red" : "brand"}
            isLoading={busy}
            isDisabled={expired}
            onClick={() => onConfirm(action)}
          >
            {undo
              ? "Undo cancellation"
              : action === "immediate"
              ? "End trial now"
              : "Cancel at trial end"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
